import React, { useState, useEffect, useContext, useCallback } from 'react';
import axios from 'axios';
import { AuthContext, API } from '../App';
import { toast } from 'sonner';
import { Bell, CheckSquare, CalendarDays, MessageCircle, Heart, VolumeX, Volume2, Filter, Mail, Phone, Monitor } from 'lucide-react';
import { Switch } from '../components/ui/switch';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Button } from '../components/ui/button';
import AppLogo from '../components/AppLogo';
import { useNavigate } from 'react-router-dom';

const NotificationControlsPage = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [prefs, setPrefs] = useState({
    tasks: true, calendar: true, updates: true, notes: true, care_pulse: true,
    muted_circles: [], priority_filter: 'all',
    notification_email: '', notification_phone: '',
    delivery_tasks: 'in_app', delivery_calendar: 'in_app', delivery_updates: 'in_app',
    delivery_notes: 'in_app', delivery_care_pulse: 'in_app'
  });
  const [circles, setCircles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [prefsRes, circlesRes] = await Promise.all([
        axios.get(`${API}/notification-preferences`),
        axios.get(`${API}/circles`)
      ]);
      setPrefs(prev => ({ ...prev, ...prefsRes.data }));
      setCircles(circlesRes.data);
    } catch (e) { toast.error('Could not load preferences'); } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const savePrefs = async (updated) => {
    setSaving(true);
    try {
      await axios.put(`${API}/notification-preferences`, updated);
      setPrefs(updated);
      toast.success('Preferences saved');
    } catch (e) { toast.error('Could not save'); } finally { setSaving(false); }
  };

  const togglePref = (key) => savePrefs({ ...prefs, [key]: !prefs[key] });
  const updateDelivery = (key, val) => savePrefs({ ...prefs, [key]: val });
  const toggleMuteCircle = (circleId) => {
    const muted = prefs.muted_circles || [];
    savePrefs({ ...prefs, muted_circles: muted.includes(circleId) ? muted.filter(id => id !== circleId) : [...muted, circleId] });
  };

  const saveContactInfo = () => savePrefs({ ...prefs });

  const NOTIFICATION_TYPES = [
    { key: 'tasks', deliveryKey: 'delivery_tasks', label: 'Help Requests & Tasks', description: 'New requests, claims, and completions', icon: CheckSquare, color: 'text-cyan-600 dark:text-cyan-400' },
    { key: 'calendar', deliveryKey: 'delivery_calendar', label: 'Calendar Events', description: 'New events and reminders', icon: CalendarDays, color: 'text-amber-600 dark:text-amber-400' },
    { key: 'updates', deliveryKey: 'delivery_updates', label: 'Circle Updates', description: 'Posts and status updates from your circle', icon: MessageCircle, color: 'text-emerald-600 dark:text-emerald-400' },
    { key: 'notes', deliveryKey: 'delivery_notes', label: 'Supportive Notes', description: 'Encouraging messages from your circle', icon: Heart, color: 'text-rose-500 dark:text-rose-400' },
    { key: 'care_pulse', deliveryKey: 'delivery_care_pulse', label: 'Care Pulse', description: 'Mood check-ins and wellness updates', icon: Heart, color: 'text-violet-500 dark:text-violet-400' },
  ];

  const DELIVERY_OPTIONS = [
    { value: 'in_app', label: 'In-App Only', icon: Monitor },
    { value: 'email', label: 'Email', icon: Mail },
    { value: 'sms', label: 'SMS / Text', icon: Phone },
    { value: 'email_and_sms', label: 'Email + SMS', icon: Bell },
    { value: 'all', label: 'All Channels', icon: Bell },
  ];

  if (loading) {
    return <div className="flex items-center justify-center min-h-screen bg-background"><div className="animate-spin rounded-full h-10 w-10 border-2 border-cyan-200 border-t-cyan-600"></div></div>;
  }

  return (
    <div className="min-h-screen pb-24 bg-background" data-testid="notification-controls-page">
      <header className="surface-header sticky top-0 z-40">
        <div className="max-w-3xl mx-auto px-5 py-3.5 flex items-center gap-3">
          <AppLogo />
          <div>
            <h1 className="text-xl font-bold text-foreground tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>Notifications</h1>
            <p className="text-xs text-muted-foreground">Control what and how you get notified</p>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-5 py-4 space-y-5">
        {/* Contact Info */}
        <section className="surface-card overflow-hidden" data-testid="contact-info">
          <div className="px-5 py-3.5 border-b border-border bg-muted/30">
            <div className="flex items-center gap-2">
              <Mail className="w-4 h-4 text-cyan-600" />
              <h3 className="text-sm font-semibold text-foreground">Delivery Contact Info</h3>
            </div>
            <p className="text-[11px] text-muted-foreground mt-0.5">Where should we send your notifications?</p>
          </div>
          <div className="px-5 py-4 space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="notif-email" className="text-xs font-medium text-foreground">Email Address</Label>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="notif-email"
                    data-testid="notification-email"
                    type="email"
                    placeholder="you@example.com"
                    value={prefs.notification_email || ''}
                    onChange={(e) => setPrefs({ ...prefs, notification_email: e.target.value })}
                    className="pl-10"
                  />
                </div>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="notif-phone" className="text-xs font-medium text-foreground">Phone Number</Label>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="notif-phone"
                    data-testid="notification-phone"
                    type="tel"
                    placeholder="+1 (555) 123-4567"
                    value={prefs.notification_phone || ''}
                    onChange={(e) => setPrefs({ ...prefs, notification_phone: e.target.value })}
                    className="pl-10"
                  />
                </div>
              </div>
            </div>
            <Button onClick={saveContactInfo} className="btn-primary w-full" disabled={saving} data-testid="save-contact-btn">
              {saving ? 'Saving...' : 'Save Contact Info'}
            </Button>
          </div>
        </section>

        {/* Per-Type Toggle + Delivery Channel */}
        <section className="surface-card overflow-hidden" data-testid="notification-types">
          <div className="px-5 py-3.5 border-b border-border bg-muted/30">
            <div className="flex items-center gap-2">
              <Bell className="w-4 h-4 text-cyan-600" />
              <h3 className="text-sm font-semibold text-foreground">Notification Types & Delivery</h3>
            </div>
          </div>
          <div className="divide-y divide-border">
            {NOTIFICATION_TYPES.map(type => {
              const Icon = type.icon;
              const enabled = prefs[type.key] !== false;
              return (
                <div key={type.key} className="px-5 py-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <Icon className={`w-5 h-5 ${type.color} flex-shrink-0`} />
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-foreground">{type.label}</p>
                        <p className="text-[11px] text-muted-foreground">{type.description}</p>
                      </div>
                    </div>
                    <Switch checked={enabled} onCheckedChange={() => togglePref(type.key)} data-testid={`toggle-${type.key}`} />
                  </div>
                  {enabled && (
                    <div className="pl-8">
                      <Label className="text-[11px] text-muted-foreground mb-1.5 block">Deliver via</Label>
                      <Select value={prefs[type.deliveryKey] || 'in_app'} onValueChange={(val) => updateDelivery(type.deliveryKey, val)}>
                        <SelectTrigger className="h-8 text-xs rounded-lg" data-testid={`delivery-${type.key}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {DELIVERY_OPTIONS.map(opt => (
                            <SelectItem key={opt.value} value={opt.value}>
                              <span className="flex items-center gap-2 text-xs">
                                <opt.icon className="w-3 h-3" /> {opt.label}
                              </span>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </section>

        {/* Priority Filter */}
        <section className="surface-card p-5" data-testid="priority-filter">
          <div className="flex items-center gap-2 mb-3">
            <Filter className="w-4 h-4 text-cyan-600" />
            <h3 className="text-sm font-semibold text-foreground">Priority Filter</h3>
          </div>
          <p className="text-xs text-muted-foreground mb-3">Only notify for tasks above a certain priority level.</p>
          <Select value={prefs.priority_filter || 'all'} onValueChange={(val) => savePrefs({ ...prefs, priority_filter: val })}>
            <SelectTrigger className="rounded-xl" data-testid="priority-filter-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All priorities</SelectItem>
              <SelectItem value="medium">Medium & High only</SelectItem>
              <SelectItem value="high">High priority only</SelectItem>
            </SelectContent>
          </Select>
        </section>

        {/* Mute Circles */}
        {circles.length > 0 && (
          <section className="surface-card overflow-hidden" data-testid="mute-circles">
            <div className="px-5 py-3.5 border-b border-border bg-muted/30">
              <div className="flex items-center gap-2">
                <VolumeX className="w-4 h-4 text-cyan-600" />
                <h3 className="text-sm font-semibold text-foreground">Mute Circles</h3>
              </div>
              <p className="text-[11px] text-muted-foreground mt-0.5">Silence notifications from specific circles.</p>
            </div>
            <div className="divide-y divide-border">
              {circles.map(circle => {
                const isMuted = (prefs.muted_circles || []).includes(circle.id);
                return (
                  <div key={circle.id} className="flex items-center justify-between px-5 py-3.5">
                    <div className="flex items-center gap-3 min-w-0">
                      {isMuted ? <VolumeX className="w-4 h-4 text-muted-foreground flex-shrink-0" /> : <Volume2 className="w-4 h-4 text-cyan-500 flex-shrink-0" />}
                      <div className="min-w-0">
                        <p className={`text-sm font-medium ${isMuted ? 'text-muted-foreground' : 'text-foreground'}`}>{circle.name}</p>
                        <p className="text-[11px] text-muted-foreground">{isMuted ? 'Muted' : 'Active'}</p>
                      </div>
                    </div>
                    <Switch checked={!isMuted} onCheckedChange={() => toggleMuteCircle(circle.id)} data-testid={`mute-circle-${circle.id}`} />
                  </div>
                );
              })}
            </div>
          </section>
        )}
      </main>
    </div>
  );
};

export default NotificationControlsPage;
